import { Link } from 'react-router-dom';
import { VenueCard, type Venue } from '../components/VenueCard';
import { ChevronRight } from 'lucide-react';
import type { ExamStatus } from '@dru/types';
import { api } from '../lib/api';
import { CLOSED_STATUSES, isUpcoming, longDate, timeOfDay, weekdayDate, workerStatus } from '../lib/labels';
import { FitnessPill } from '../components/FitnessMark';
import { useQuery } from '../lib/useQuery';
import { useOfflineQuery } from '../lib/useOfflineQuery';
import { Journey } from '../components/Journey';

/**
 * Home is an ACTION surface, not a status board.
 *
 * The competitive read on UNO.care landed on this: a worker opening the app
 * wants to know what they must do, then where their checkup is. Anything that
 * needs a decision from them comes first.
 */

interface MeExam {
  id: string;
  examinationType: string;
  status: ExamStatus;
  dueDate: string | null;
  appointments: Array<{
    id: string; scheduledFor: string; venueType: string; status: string;
    venue: Venue | null;
  }>;
}

interface MeCert {
  id: string;
  certificateNumber: string;
  decision: string;
  validUntil: string;
  status: string;
  /** Decided by the server, so Home and Fitness cannot disagree. */
  current: boolean;
}

function Card({ children, tone = 'plain' }: {
  children: React.ReactNode;
  tone?: 'plain' | 'action' | 'warn';
}) {
  return (
    <div className={`rounded-xl border p-4 ${
      tone === 'action' ? 'border-primary-200 bg-primary-50'
      : tone === 'warn' ? 'border-dueSoon-600/30 bg-dueSoon-100'
      : 'border-neutral-200 bg-white'
    }`}>
      {children}
    </div>
  );
}

export function Home() {
  const exams = useOfflineQuery<MeExam[]>('examinations', () => api.get('/me/examinations'));
  const consents = useQuery<Array<{ id: string; examinationType: string }>>(
    () => api.get('/me/consents/pending'), []);
  const certs = useOfflineQuery<MeCert[]>('certificates', () => api.get('/me/certificates'));

  const current = exams.data?.find((e) => !CLOSED_STATUSES.includes(e.status));
  // Was `certs.data?.[0]`, which is the most recently issued certificate — not
  // necessarily a valid one. A worker whose newest certificate had been revoked
  // saw it here as though it were their standing status, while Fitness showed
  // them a different one.
  const cert = certs.data?.find((c) => c.current);
  const pendingConsent = consents.data?.[0];
  // Only a visit still ahead is an instruction. A past one is history, and
  // the journey below already says what happened after it.
  const nextAppt = current?.appointments?.find((a) => isUpcoming(a.scheduledFor));
  const certExpired = cert ? new Date(cert.validUntil) < new Date() : false;

  return (
    <>
    {/* The name is in the header already; the heading is for the page
        outline a screen reader walks, not for the eye. Outside the stack, or
        the invisible heading still takes the first gap. */}
    <h1 className="sr-only">Home</h1>
    <div className="space-y-4">

      {/* Anything needing a decision comes first — a consent that blocks a
          checkup is the one thing a worker can unblock themselves. */}
      {pendingConsent && (
        <Card tone="action">
          <p className="text-sm font-semibold text-neutral-900">Your consent is needed</p>
          <p className="mt-1 text-sm text-neutral-700">
            Your health checkup cannot go ahead until you agree to it. This takes under a minute.
          </p>
          <Link
            to={`/consent/${pendingConsent.id}`}
            className="mt-3 flex min-h-12 items-center justify-center rounded-lg bg-primary-600 px-4 text-center text-base font-medium text-white active:bg-primary-700"
          >
            Review and agree
          </Link>
        </Card>
      )}

      {nextAppt && (
        <Card>
          <h2 className="text-xs font-medium uppercase tracking-wide text-neutral-600">
            Your appointment
          </h2>
          <p className="mt-1 text-lg font-semibold text-neutral-900">
            {weekdayDate(nextAppt.scheduledFor)}
          </p>
          <p className="text-sm text-neutral-700">
            {timeOfDay(nextAppt.scheduledFor)}
          </p>

          {/* Where, and how to get there. This card used to give a worker a
              date and a time and nothing else — somebody took unpaid leave
              and had to ring their supervisor to find out which hospital. */}
          <VenueCard venue={nextAppt.venue} />

          <p className="mt-3 text-sm text-neutral-700">
            Carry a photo ID. You will be checked in at the desk.
          </p>
        </Card>
      )}

      <Card>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-neutral-900">Your checkup</h2>
          {current && (
            <span className="text-right text-xs text-neutral-600">
              {workerStatus(current.status)}
            </span>
          )}
        </div>

        {exams.loading ? (
          <div role="status" aria-label="Loading" className="h-24 animate-pulse rounded-lg bg-neutral-100" />
        ) : current ? (
          <Journey status={current.status} />
        ) : (
          <p className="text-sm text-neutral-600">
            You have no checkup in progress. Your employer will arrange the next one when it
            is due.
          </p>
        )}
      </Card>

      {cert && (
        <Link to="/fitness" className="block rounded-xl">
          <Card>
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-xs font-medium uppercase tracking-wide text-neutral-600">
                  Your fitness certificate
                </h2>
                {/* The decision, not only the number: this card used to show a
                    certificate number and a date, and a worker had to open it
                    to learn whether they were cleared to work. */}
                <div className="mt-2">
                  {certExpired
                    ? <span className="inline-flex items-center rounded-full bg-overdue-100 px-2.5 py-1 text-sm font-semibold text-overdue-600">Expired</span>
                    : <FitnessPill decision={cert.decision} />}
                </div>
                <p className="mt-2 text-sm text-neutral-700">
                  {/* A date, not the timestamp the API sends: "2027-09-05T00:00:00.000Z"
                      is not something a worker at a gate can read. */}
                  {certExpired ? 'Expired on' : 'Valid until'} {longDate(cert.validUntil)}
                </p>
                <p className="mt-0.5 font-mono text-xs text-neutral-600">{cert.certificateNumber}</p>
              </div>
              <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-neutral-400" />
            </div>
            <p className="mt-2 text-xs font-medium text-primary-700">View and show at the gate</p>
          </Card>
        </Link>
      )}

      {/* Announcements last: what the worker must do and where their checkup
          stands come first, and a banner never pushes either off the screen.
          Renders nothing when there is nothing to show. */}
    </div>
    </>
  );
}
